import { Check, CircleAlert, Cloud, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SaveStatus } from "./use-form-runtime";

// Top-right confidence signal for the form-detail page. Monochrome by
// design — only the error state pops with `text-destructive`. The label
// reads naturally so a screen reader can announce it without aria fuss.

type Props = {
  status: SaveStatus;
  className?: string;
};

export function SaveStatusIndicator({ status, className }: Props) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-xs",
        status === "error" ? "text-destructive" : "text-muted-foreground",
        className,
      )}
      role="status"
      aria-live="polite"
    >
      {iconFor(status)}
      {labelFor(status)}
    </span>
  );
}

function iconFor(status: SaveStatus) {
  switch (status) {
    case "saving":
      return <Loader2 className="h-3.5 w-3.5 animate-spin" />;
    case "saved":
      return <Check className="h-3.5 w-3.5" />;
    case "error":
      return <CircleAlert className="h-3.5 w-3.5" />;
    case "idle":
    default:
      return <Cloud className="h-3.5 w-3.5" />;
  }
}

function labelFor(status: SaveStatus): string {
  switch (status) {
    case "saving":
      return "Saving…";
    case "saved":
      return "Saved";
    case "error":
      return "Save failed";
    case "idle":
    default:
      return "Draft";
  }
}
